/**
 * Firestore helpers for CMS: pages, home setting, and SEO overrides.
 * Collections: artifacts/{appId}/cmsPages, artifacts/{appId}/settings/home, artifacts/{appId}/seoOverrides
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
  type DocumentData,
  type Timestamp,
} from 'firebase/firestore';
import { db, appId } from './firebase';

export type CMSPageType = 'home' | 'landing' | 'legal' | 'generic';
export type CMSPageStatus = 'draft' | 'published';

export interface CMSPage {
  id: string;
  slug: string;
  title: string;
  body: string;
  type: CMSPageType;
  status: CMSPageStatus;
  /** When true (default), the page title is shown above the body on the front-end. */
  showTitle?: boolean;
  publishedAt?: Timestamp | Date | null;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  createdBy?: string | null;
  order?: number | null;
  path?: string | null; // optional full path e.g. /solutions, /lp/promo
}

export interface HomeSetting {
  pageId?: string | null;
  slug?: string | null;
  updatedAt?: Timestamp | Date | null;
  updatedBy?: string | null;
}

export interface SEOOverride {
  path?: string | null;
  pageId?: string | null;
  title?: string | null;
  description?: string | null;
  keywords?: string | null;
  ogImage?: string | null;
  ogTitle?: string | null;
  ogDescription?: string | null;
  twitterCard?: string | null;
  twitterTitle?: string | null;
  twitterDescription?: string | null;
  canonical?: string | null;
  noIndex?: boolean | null;
  schemaJson?: string | null;
}

export type AIProvider = 'openai' | 'anthropic' | 'gemini';

export interface AIConfig {
  provider: AIProvider;
  apiKey: string;
  model: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatedAt?: any;
  updatedBy?: string;
}

const cmsPagesRef = () => collection(db, 'artifacts', appId, 'cmsPages');
const settingsHomeRef = () => doc(db, 'artifacts', appId, 'settings', 'home');
const settingsAIRef = () => doc(db, 'artifacts', appId, 'settings', 'aiConfig');

function toCMSPage(id: string, data: DocumentData): CMSPage {
  return {
    id,
    slug: data.slug ?? '',
    title: data.title ?? '',
    body: data.body ?? '',
    type: (data.type as CMSPageType) ?? 'generic',
    status: (data.status as CMSPageStatus) ?? 'draft',
    showTitle: data.showTitle !== false,
    publishedAt: data.publishedAt ?? null,
    createdAt: data.createdAt ?? new Date(),
    updatedAt: data.updatedAt ?? new Date(),
    createdBy: data.createdBy ?? null,
    order: data.order ?? null,
    path: data.path ?? null,
  };
}

/**
 * Read the current home page setting (pageId or slug).
 */
export async function getHomeSetting(): Promise<HomeSetting | null> {
  const snap = await getDoc(settingsHomeRef());
  if (!snap.exists()) return null;
  return snap.data() as HomeSetting;
}

/**
 * Get a single CMS page by document id.
 */
export async function getCMSPageById(pageId: string): Promise<CMSPage | null> {
  const ref = doc(db, 'artifacts', appId, 'cmsPages', pageId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return toCMSPage(snap.id, snap.data());
}

/**
 * Get a single CMS page by slug (first match).
 * Tries slug as-is, then with leading slash, so both "home-1" and "/home-1" in DB work.
 */
export async function getCMSPageBySlug(slug: string): Promise<CMSPage | null> {
  const normalized = slug.trim();
  for (const candidate of [normalized, normalized.startsWith('/') ? normalized : `/${normalized}`]) {
    const q = query(cmsPagesRef(), where('slug', '==', candidate), where('status', '==', 'published'));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const docSnap = snapshot.docs[0];
      return toCMSPage(docSnap.id, docSnap.data());
    }
  }
  return null;
}

/**
 * Get the CMS page that is currently set as the homepage.
 */
export async function getHomePage(): Promise<CMSPage | null> {
  const home = await getHomeSetting();
  if (!home) return null;
  if (home.pageId) return getCMSPageById(home.pageId);
  if (home.slug) return getCMSPageBySlug(home.slug);
  return null;
}

/**
 * List published landing pages (type === 'landing', status === 'published').
 * Returns slugs for routing /lp/:slug.
 */
export async function getPublishedLandingSlugs(): Promise<string[]> {
  const q = query(
    cmsPagesRef(),
    where('type', '==', 'landing'),
    where('status', '==', 'published'),
    orderBy('updatedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => (d.data().slug as string) ?? d.id).filter(Boolean);
}

/**
 * List all CMS pages (for admin).
 */
export async function getAllCMSPages(): Promise<CMSPage[]> {
  const q = query(cmsPagesRef(), orderBy('updatedAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => toCMSPage(d.id, d.data()));
}

/**
 * Get a CMS page by slug (any status) for admin uniqueness checks.
 * Returns the page that has this slug, or null. Tries slug as-is and with leading slash.
 */
export async function getCMSPageBySlugForAdmin(slug: string): Promise<CMSPage | null> {
  const normalized = slug.trim();
  const candidates =
    normalized.startsWith('/') ? [normalized] : [normalized, `/${normalized}`];
  for (const candidate of candidates) {
    const q = query(cmsPagesRef(), where('slug', '==', candidate));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const docSnap = snapshot.docs[0];
      return toCMSPage(docSnap.id, docSnap.data());
    }
  }
  return null;
}

/** Encode path to a valid Firestore document id: "/" -> "home", "/lp/xyz" -> "lp_xyz". */
export function pathToSeoDocId(path: string): string {
  const p = path || '/';
  if (p === '/') return 'home';
  return p.slice(1).replace(/\//g, '_');
}

/**
 * Get SEO override for a path (doc id from pathToSeoDocId).
 */
export async function getSEOOverrideByPath(path: string): Promise<SEOOverride | null> {
  const docId = pathToSeoDocId(path);
  const ref = doc(db, 'artifacts', appId, 'seoOverrides', docId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    path: data.path ?? path,
    pageId: data.pageId ?? null,
    title: data.title ?? null,
    description: data.description ?? null,
    keywords: data.keywords ?? null,
    ogImage: data.ogImage ?? null,
    ogTitle: data.ogTitle ?? null,
    ogDescription: data.ogDescription ?? null,
    twitterCard: data.twitterCard ?? null,
    twitterTitle: data.twitterTitle ?? null,
    twitterDescription: data.twitterDescription ?? null,
    canonical: data.canonical ?? null,
    noIndex: data.noIndex ?? null,
    schemaJson: data.schemaJson ?? null,
  };
}

/**
 * Get SEO override by page id (doc id = pageId).
 */
export async function getSEOOverrideByPageId(pageId: string): Promise<SEOOverride | null> {
  const ref = doc(db, 'artifacts', appId, 'seoOverrides', pageId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    path: data.path ?? null,
    pageId: data.pageId ?? pageId,
    title: data.title ?? null,
    description: data.description ?? null,
    keywords: data.keywords ?? null,
    ogImage: data.ogImage ?? null,
    ogTitle: data.ogTitle ?? null,
    ogDescription: data.ogDescription ?? null,
    twitterCard: data.twitterCard ?? null,
    twitterTitle: data.twitterTitle ?? null,
    twitterDescription: data.twitterDescription ?? null,
    canonical: data.canonical ?? null,
    noIndex: data.noIndex ?? null,
    schemaJson: data.schemaJson ?? null,
  };
}

/** Payload for creating/updating a CMS page. */
export interface CMSPagePayload {
  slug: string;
  title: string;
  body: string;
  type: CMSPageType;
  status: CMSPageStatus;
  /** When true (default), show the page title on the front-end. */
  showTitle?: boolean;
  order?: number | null;
  path?: string | null;
}

/** Remove undefined values from an object. Firestore does not allow undefined. */
function omitUndefined<T extends Record<string, unknown>>(obj: T): { [K in keyof T]: T[K] } {
  const out = {} as { [K in keyof T]: T[K] };
  for (const key of Object.keys(obj) as (keyof T)[]) {
    if (obj[key] !== undefined) {
      (out as Record<string, unknown>)[key as string] = obj[key];
    }
  }
  return out;
}

/**
 * Create a new CMS page. Returns the new page id.
 */
export async function createCMSPage(payload: CMSPagePayload, createdBy: string): Promise<string> {
  const ref = collection(db, 'artifacts', appId, 'cmsPages');
  const data = omitUndefined({
    ...payload,
    publishedAt: payload.status === 'published' ? serverTimestamp() : null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy,
  });
  const docRef = await addDoc(ref, data);
  return docRef.id;
}

/**
 * Update an existing CMS page.
 */
export async function updateCMSPage(
  pageId: string,
  payload: Partial<CMSPagePayload>,
  updatedBy: string
): Promise<void> {
  const ref = doc(db, 'artifacts', appId, 'cmsPages', pageId);
  const existing = await getDoc(ref);
  if (!existing.exists()) throw new Error('Page not found');
  const data = existing.data();
  const newStatus = payload.status ?? data.status;
  const updateData = omitUndefined({
    ...payload,
    updatedAt: serverTimestamp(),
    updatedBy,
    ...(newStatus === 'published' && data.status !== 'published'
      ? { publishedAt: serverTimestamp() }
      : {}),
  });
  await setDoc(ref, updateData, { merge: true });
}

/**
 * Delete a CMS page.
 */
export async function deleteCMSPage(pageId: string): Promise<void> {
  const ref = doc(db, 'artifacts', appId, 'cmsPages', pageId);
  await deleteDoc(ref);
}

/**
 * Set the current home page (by pageId or slug).
 */
export async function setHomePage(
  by: { pageId?: string; slug?: string },
  updatedBy: string
): Promise<void> {
  await setDoc(settingsHomeRef(), {
    ...by,
    updatedAt: serverTimestamp(),
    updatedBy,
  });
}

/**
 * Clear the current home page setting. The site root (/) will then show the hardcoded home page.
 */
export async function clearHomePage(): Promise<void> {
  await deleteDoc(settingsHomeRef());
}

/**
 * Save SEO override for a path or page. Doc id = pathToSeoDocId(path) or pageId.
 */
export async function saveSEOOverride(
  docId: string,
  data: Partial<SEOOverride>,
  path?: string
): Promise<void> {
  const ref = doc(db, 'artifacts', appId, 'seoOverrides', docId);
  await setDoc(ref, { ...data, path: path ?? docId }, { merge: true });
}

/**
 * List all SEO override doc ids (for admin). Uses collection list.
 */
export async function listSEOOverrideDocIds(): Promise<string[]> {
  const ref = collection(db, 'artifacts', appId, 'seoOverrides');
  const snapshot = await getDocs(ref);
  return snapshot.docs.map((d) => d.id);
}

/**
 * List SEO overrides with summary fields (id, title, path) for admin table display.
 * Single collection fetch — not N+1.
 */
export async function listSEOOverrides(): Promise<{ id: string; title?: string | null; path?: string | null; noIndex?: boolean | null }[]> {
  const ref = collection(db, 'artifacts', appId, 'seoOverrides');
  const snapshot = await getDocs(ref);
  return snapshot.docs.map((d) => ({
    id: d.id,
    title: d.data().title ?? null,
    path: d.data().path ?? null,
    noIndex: d.data().noIndex ?? null,
  }));
}

/**
 * Delete an SEO override by doc id.
 */
export async function deleteSEOOverride(docId: string): Promise<void> {
  const ref = doc(db, 'artifacts', appId, 'seoOverrides', docId);
  await deleteDoc(ref);
}

// ---------------------------------------------------------------------------
// FAQ Items
// Collection: artifacts/{appId}/faqs
// ---------------------------------------------------------------------------

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  order: number;
  isPublished: boolean;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  createdBy?: string | null;
}

export interface FAQItemPayload {
  question: string;
  answer: string;
  order?: number;
  isPublished?: boolean;
}

const faqsRef = () => collection(db, 'artifacts', appId, 'faqs');

function toFAQItem(id: string, data: DocumentData): FAQItem {
  return {
    id,
    question: data.question ?? '',
    answer: data.answer ?? '',
    order: data.order ?? 0,
    isPublished: data.isPublished ?? true,
    createdAt: data.createdAt ?? new Date(),
    updatedAt: data.updatedAt ?? new Date(),
    createdBy: data.createdBy ?? null,
  };
}

/** List all FAQ items ordered by `order` asc (for admin). */
export async function getAllFAQItems(): Promise<FAQItem[]> {
  const q = query(faqsRef(), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => toFAQItem(d.id, d.data()));
}

/** List published FAQ items ordered by `order` asc (for public display). */
export async function getPublishedFAQItems(): Promise<FAQItem[]> {
  const q = query(faqsRef(), where('isPublished', '==', true), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => toFAQItem(d.id, d.data()));
}

/** Create a new FAQ item. Returns the new document id. */
export async function createFAQItem(payload: FAQItemPayload, createdBy: string): Promise<string> {
  const docRef = await addDoc(faqsRef(), {
    ...payload,
    order: payload.order ?? 0,
    isPublished: payload.isPublished ?? true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy,
  });
  return docRef.id;
}

/** Update an existing FAQ item. */
export async function updateFAQItem(
  faqId: string,
  payload: Partial<FAQItemPayload>,
  updatedBy: string
): Promise<void> {
  const ref = doc(db, 'artifacts', appId, 'faqs', faqId);
  await setDoc(ref, { ...payload, updatedAt: serverTimestamp(), updatedBy }, { merge: true });
}

/** Delete a FAQ item. */
export async function deleteFAQItem(faqId: string): Promise<void> {
  const ref = doc(db, 'artifacts', appId, 'faqs', faqId);
  await deleteDoc(ref);
}

/** Batch-update the `order` field for a list of FAQ ids. */
export async function reorderFAQItems(orderedIds: string[], updatedBy: string): Promise<void> {
  await Promise.all(
    orderedIds.map((id, index) =>
      setDoc(
        doc(db, 'artifacts', appId, 'faqs', id),
        { order: index, updatedAt: serverTimestamp(), updatedBy },
        { merge: true }
      )
    )
  );
}

/** The embed shortcode users paste into CMS page bodies. */
export const FAQ_EMBED_CODE = '<div data-smbprime-embed="faq-section"></div>';

/** Blog articles embed shortcode — renders the blog listing inside any CMS page body. */
export const BLOG_ARTICLES_EMBED_CODE = '<div data-smbprime-embed="blog-articles"></div>';

/** Featured blog articles embed — renders only published articles marked as featured. */
export const BLOG_FEATURED_ARTICLES_EMBED_CODE = '<div data-smbprime-embed="blog-articles-featured"></div>';

// ---------------------------------------------------------------------------
// Navigation — NavGroups & NavItems
// Collections: artifacts/{appId}/navGroups, artifacts/{appId}/navItems
// ---------------------------------------------------------------------------

export type NavItemType = 'link' | 'dropdown' | 'button' | 'embed' | 'text' | 'html';
export type NavTarget = '_self' | '_blank';
export type NavButtonStyle = 'primary' | 'secondary' | 'outline';
export type NavPlacement = 'header' | 'footer';

/** A named group/column that owns a set of nav items (e.g. "Main Menu", "Footer — Company"). */
export interface NavGroup {
  id: string;
  name: string;
  placement: NavPlacement;
  order: number;
  isVisible: boolean;
  description?: string | null;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  createdBy?: string | null;
}

export interface NavGroupPayload {
  name: string;
  placement: NavPlacement;
  order?: number;
  isVisible?: boolean;
  description?: string | null;
}

/** A single navigation item belonging to a group; parentId enables dropdown nesting. */
export interface NavItem {
  id: string;
  label: string;
  /** External href. Mutually exclusive with slug. */
  url?: string | null;
  /** Internal CMS page slug — renders as <Link to={slug}>. Mutually exclusive with url. */
  slug?: string | null;
  type: NavItemType;
  /** null = top-level item; set to another navItem's id for dropdown children. */
  parentId?: string | null;
  /** ID of the NavGroup this item belongs to. */
  groupId?: string | null;
  placement: NavPlacement;
  order: number;
  isVisible: boolean;
  target?: NavTarget | null;
  buttonStyle?: NavButtonStyle | null;
  /** Raw HTML injected inline for type === 'embed'. */
  embedCode?: string | null;
  /** Optional icon name (Lucide icon key from NAV_ICON_MAP) or emoji string. */
  icon?: string | null;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  createdBy?: string | null;
}

export interface NavItemPayload {
  label: string;
  url?: string | null;
  slug?: string | null;
  type: NavItemType;
  parentId?: string | null;
  groupId?: string | null;
  placement: NavPlacement;
  order?: number;
  isVisible?: boolean;
  target?: NavTarget | null;
  buttonStyle?: NavButtonStyle | null;
  embedCode?: string | null;
  /** Optional icon name (Lucide icon key from NAV_ICON_MAP) or emoji string. */
  icon?: string | null;
}

const navGroupsRef = () => collection(db, 'artifacts', appId, 'navGroups');
const navItemsRef = () => collection(db, 'artifacts', appId, 'navItems');

function toNavGroup(id: string, data: DocumentData): NavGroup {
  return {
    id,
    name: data.name ?? '',
    placement: (data.placement as NavPlacement) ?? 'header',
    order: data.order ?? 0,
    isVisible: data.isVisible !== false,
    description: data.description ?? null,
    createdAt: data.createdAt ?? new Date(),
    updatedAt: data.updatedAt ?? new Date(),
    createdBy: data.createdBy ?? null,
  };
}

function toNavItem(id: string, data: DocumentData): NavItem {
  return {
    id,
    label: data.label ?? '',
    url: data.url ?? null,
    slug: data.slug ?? null,
    type: (data.type as NavItemType) ?? 'link',
    parentId: data.parentId ?? null,
    groupId: data.groupId ?? null,
    placement: (data.placement as NavPlacement) ?? 'header',
    order: data.order ?? 0,
    isVisible: data.isVisible !== false,
    target: (data.target as NavTarget) ?? null,
    buttonStyle: (data.buttonStyle as NavButtonStyle) ?? null,
    embedCode: data.embedCode ?? null,
    icon: data.icon ?? null,
    createdAt: data.createdAt ?? new Date(),
    updatedAt: data.updatedAt ?? new Date(),
    createdBy: data.createdBy ?? null,
  };
}

/** List all nav groups ordered by order asc. */
export async function getAllNavGroups(): Promise<NavGroup[]> {
  const q = query(navGroupsRef(), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => toNavGroup(d.id, d.data()));
}

/** List nav groups by placement ordered by order asc. */
export async function getNavGroupsByPlacement(placement: NavPlacement): Promise<NavGroup[]> {
  const q = query(navGroupsRef(), where('placement', '==', placement), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => toNavGroup(d.id, d.data()));
}

/** Create a new nav group. Returns the new document id. */
export async function createNavGroup(payload: NavGroupPayload, createdBy: string): Promise<string> {
  const docRef = await addDoc(navGroupsRef(), {
    ...omitUndefined({ ...payload } as Record<string, unknown>),
    order: payload.order ?? 0,
    isVisible: payload.isVisible !== false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy,
  });
  return docRef.id;
}

/** Update an existing nav group. */
export async function updateNavGroup(
  groupId: string,
  payload: Partial<NavGroupPayload>,
  updatedBy: string
): Promise<void> {
  const ref = doc(db, 'artifacts', appId, 'navGroups', groupId);
  await setDoc(ref, { ...omitUndefined({ ...payload } as Record<string, unknown>), updatedAt: serverTimestamp(), updatedBy }, { merge: true });
}

/** Delete a nav group (does not delete its items — call deleteNavItemsByGroup separately). */
export async function deleteNavGroup(groupId: string): Promise<void> {
  await deleteDoc(doc(db, 'artifacts', appId, 'navGroups', groupId));
}

/** Batch-update the order field for nav groups. */
export async function reorderNavGroups(orderedIds: string[], updatedBy: string): Promise<void> {
  await Promise.all(
    orderedIds.map((id, index) =>
      setDoc(doc(db, 'artifacts', appId, 'navGroups', id), { order: index, updatedAt: serverTimestamp(), updatedBy }, { merge: true })
    )
  );
}

/** List all nav items for a given group ordered by order asc. */
export async function getNavItemsByGroup(groupId: string): Promise<NavItem[]> {
  const q = query(navItemsRef(), where('groupId', '==', groupId), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => toNavItem(d.id, d.data()));
}

/** List all nav items for a given placement (for public rendering). */
export async function getNavItemsByPlacement(placement: NavPlacement): Promise<NavItem[]> {
  const q = query(navItemsRef(), where('placement', '==', placement), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => toNavItem(d.id, d.data()));
}

/** List all nav items (admin). */
export async function getAllNavItems(): Promise<NavItem[]> {
  const q = query(navItemsRef(), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => toNavItem(d.id, d.data()));
}

/** Create a new nav item. Returns the new document id. */
export async function createNavItem(payload: NavItemPayload, createdBy: string): Promise<string> {
  const docRef = await addDoc(navItemsRef(), {
    ...omitUndefined({ ...payload } as Record<string, unknown>),
    order: payload.order ?? 0,
    isVisible: payload.isVisible !== false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy,
  });
  return docRef.id;
}

/** Update an existing nav item. */
export async function updateNavItem(
  itemId: string,
  payload: Partial<NavItemPayload>,
  updatedBy: string
): Promise<void> {
  const ref = doc(db, 'artifacts', appId, 'navItems', itemId);
  await setDoc(ref, { ...omitUndefined({ ...payload } as Record<string, unknown>), updatedAt: serverTimestamp(), updatedBy }, { merge: true });
}

/** Delete a nav item. */
export async function deleteNavItem(itemId: string): Promise<void> {
  await deleteDoc(doc(db, 'artifacts', appId, 'navItems', itemId));
}

/** Batch-update the order field for nav items. */
export async function reorderNavItems(orderedIds: string[], updatedBy: string): Promise<void> {
  await Promise.all(
    orderedIds.map((id, index) =>
      setDoc(doc(db, 'artifacts', appId, 'navItems', id), { order: index, updatedAt: serverTimestamp(), updatedBy }, { merge: true })
    )
  );
}

// ---------------------------------------------------------------------------
// Header Config (singleton: artifacts/{appId}/settings/headerConfig)
// ---------------------------------------------------------------------------

export interface CTAButton {
  id: string;
  label: string;
  url?: string | null;
  /** 'open-strategy-call' fires the strategy call modal; 'external'/'internal' navigates. */
  action?: 'open-strategy-call' | 'external' | 'internal' | null;
  style: NavButtonStyle;
  order: number;
  isVisible: boolean;
}

export interface HeaderConfig {
  logoUrl?: string | null;
  tagline?: string | null;
  showContactPhone: boolean;
  contactPhone?: string | null;
  ctaButtons: CTAButton[];
  showProfileButton: boolean;
  showAdminLink: boolean;
  updatedAt?: Timestamp | Date | null;
  updatedBy?: string | null;
}

const settingsHeaderConfigRef = () => doc(db, 'artifacts', appId, 'settings', 'headerConfig');

/** Get current header config (returns null if not yet set). */
export async function getHeaderConfig(): Promise<HeaderConfig | null> {
  const snap = await getDoc(settingsHeaderConfigRef());
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    logoUrl: d.logoUrl ?? null,
    tagline: d.tagline ?? null,
    showContactPhone: d.showContactPhone !== false,
    contactPhone: d.contactPhone ?? null,
    ctaButtons: (d.ctaButtons as CTAButton[]) ?? [],
    showProfileButton: d.showProfileButton !== false,
    showAdminLink: d.showAdminLink !== false,
    updatedAt: d.updatedAt ?? null,
    updatedBy: d.updatedBy ?? null,
  };
}

/** Save (merge) header config. */
export async function saveHeaderConfig(
  data: Partial<Omit<HeaderConfig, 'updatedAt' | 'updatedBy'>>,
  updatedBy: string
): Promise<void> {
  await setDoc(settingsHeaderConfigRef(), { ...omitUndefined({ ...data } as Record<string, unknown>), updatedAt: serverTimestamp(), updatedBy }, { merge: true });
}

// ---------------------------------------------------------------------------
// Footer Config (singleton: artifacts/{appId}/settings/footerConfig)
// ---------------------------------------------------------------------------

export interface FooterSocialLink {
  id: string;
  platform: 'facebook' | 'linkedin' | 'twitter' | 'instagram' | 'youtube' | 'tiktok';
  url: string;
  isVisible: boolean;
}

export interface FooterConfig {
  showLeadCaptureBanner: boolean;
  showWebsiteCTABand: boolean;
  /** When true, a newsletter subscription band is rendered in the footer. */
  showNewsletterSection?: boolean;
  /** Heading text for the newsletter band (default: "Stay in the loop"). */
  newsletterHeading?: string | null;
  /** Description text shown beneath the heading in the newsletter band. */
  newsletterDescription?: string | null;
  copyrightText?: string | null;
  brandDescription?: string | null;
  socialLinks: FooterSocialLink[];
  /** Ordered list of NavGroup IDs to render as footer columns. */
  footerNavGroupIds: string[];
  updatedAt?: Timestamp | Date | null;
  updatedBy?: string | null;
}

const settingsFooterConfigRef = () => doc(db, 'artifacts', appId, 'settings', 'footerConfig');

/** Get current footer config (returns null if not yet set). */
export async function getFooterConfig(): Promise<FooterConfig | null> {
  const snap = await getDoc(settingsFooterConfigRef());
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    showLeadCaptureBanner: d.showLeadCaptureBanner !== false,
    showWebsiteCTABand: d.showWebsiteCTABand !== false,
    showNewsletterSection: d.showNewsletterSection === true,
    newsletterHeading: d.newsletterHeading ?? null,
    newsletterDescription: d.newsletterDescription ?? null,
    copyrightText: d.copyrightText ?? null,
    brandDescription: d.brandDescription ?? null,
    socialLinks: (d.socialLinks as FooterSocialLink[]) ?? [],
    footerNavGroupIds: (d.footerNavGroupIds as string[]) ?? [],
    updatedAt: d.updatedAt ?? null,
    updatedBy: d.updatedBy ?? null,
  };
}

/** Save (merge) footer config. */
export async function saveFooterConfig(
  data: Partial<Omit<FooterConfig, 'updatedAt' | 'updatedBy'>>,
  updatedBy: string
): Promise<void> {
  await setDoc(settingsFooterConfigRef(), { ...omitUndefined({ ...data } as Record<string, unknown>), updatedAt: serverTimestamp(), updatedBy }, { merge: true });
}

// ---------------------------------------------------------------------------
// Link Preview (singleton: artifacts/{appId}/settings/linkPreview)
// Used by LinkedIn, Facebook, Twitter when the site URL is shared. Admin can set default og:image, author, publish date.
// ---------------------------------------------------------------------------

export interface LinkPreviewSettings {
  /** Default og:image URL (e.g. https://smbprime.ai/og-image.png). Used when no page-specific SEO override provides an image. */
  ogImage?: string | null;
  /** Meta author (e.g. "SMBPrime"). Shown in crawler metadata. */
  author?: string | null;
  /** Optional publish date (ISO string). Some platforms show article:published_time. */
  publishDate?: string | null;
  updatedAt?: Timestamp | Date | null;
  updatedBy?: string | null;
}

const settingsLinkPreviewRef = () => doc(db, 'artifacts', appId, 'settings', 'linkPreview');

/** Get link preview settings (returns null if not yet set). */
export async function getLinkPreviewSettings(): Promise<LinkPreviewSettings | null> {
  const snap = await getDoc(settingsLinkPreviewRef());
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    ogImage: d.ogImage ?? null,
    author: d.author ?? null,
    publishDate: d.publishDate ?? null,
    updatedAt: d.updatedAt ?? null,
    updatedBy: d.updatedBy ?? null,
  };
}

/** Save (merge) link preview settings. */
export async function saveLinkPreviewSettings(
  data: Partial<Omit<LinkPreviewSettings, 'updatedAt' | 'updatedBy'>>,
  updatedBy: string
): Promise<void> {
  await setDoc(settingsLinkPreviewRef(), { ...omitUndefined({ ...data } as Record<string, unknown>), updatedAt: serverTimestamp(), updatedBy }, { merge: true });
}

// ---------------------------------------------------------------------------
// Header / Footer custom HTML overrides
// Singletons: artifacts/{appId}/settings/headerHtml  and  .../footerHtml
// When set, the public site renders this raw HTML instead of the React template.
// ---------------------------------------------------------------------------

const settingsHeaderHtmlRef = () => doc(db, 'artifacts', appId, 'settings', 'headerHtml');
const settingsFooterHtmlRef = () => doc(db, 'artifacts', appId, 'settings', 'footerHtml');

/** Get the custom header HTML override (null = use default React header). */
export async function getHeaderHtml(): Promise<string | null> {
  const snap = await getDoc(settingsHeaderHtmlRef());
  if (!snap.exists()) return null;
  return (snap.data().html as string) ?? null;
}

/** Save (or clear) the custom header HTML override. Pass null / empty string to revert to default. */
export async function saveHeaderHtml(html: string | null, updatedBy?: string): Promise<void> {
  await setDoc(settingsHeaderHtmlRef(), omitUndefined({
    html: html?.trim() || null,
    updatedAt: serverTimestamp(),
    updatedBy: updatedBy ?? null,
  }));
}

/** Get the custom footer HTML override (null = use default React footer). */
export async function getFooterHtml(): Promise<string | null> {
  const snap = await getDoc(settingsFooterHtmlRef());
  if (!snap.exists()) return null;
  return (snap.data().html as string) ?? null;
}

/** Save (or clear) the custom footer HTML override. Pass null / empty string to revert to default. */
export async function saveFooterHtml(html: string | null, updatedBy?: string): Promise<void> {
  await setDoc(settingsFooterHtmlRef(), omitUndefined({
    html: html?.trim() || null,
    updatedAt: serverTimestamp(),
    updatedBy: updatedBy ?? null,
  }));
}

/**
 * Read AI configuration (provider, apiKey, model) stored at
 * artifacts/{appId}/settings/aiConfig. Returns null if not yet configured.
 */
export async function getAIConfig(): Promise<AIConfig | null> {
  const snap = await getDoc(settingsAIRef());
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    provider: (data.provider as AIProvider) ?? 'openai',
    apiKey: data.apiKey ?? '',
    model: data.model ?? '',
    updatedAt: data.updatedAt ?? null,
    updatedBy: data.updatedBy ?? null,
  };
}

/**
 * Save AI configuration to Firestore.
 */
export async function saveAIConfig(config: AIConfig): Promise<void> {
  await setDoc(settingsAIRef(), {
    provider: config.provider,
    apiKey: config.apiKey,
    model: config.model,
    updatedAt: serverTimestamp(),
    updatedBy: config.updatedBy ?? null,
  });
}
